/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: caoran
 * @LastEditTime: 2025-06-23 20:51:41
 */
import './index.scss'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
  Layout,
  Input,
  Button,
  Form,
  Row,
  Select,
  Spin,
  Modal,
  Tooltip,
  message,
  Empty
} from 'antd'
import { CloseOutlined, PictureOutlined } from '@ant-design/icons'
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import {
  getWorkgroupPhotos,
  getWorkgroupPhotosByAiSearch
} from '../../actions/aiSearchPhoto'
import dayjs from 'dayjs'
import CONFIG from '../../constants/config'
const { Content } = Layout
const { Option } = Select
const FormItem = Form.Item

// 照片类型定义
interface Photo {
  file: string
  smallURL: string
  largeURL: string
  saveName?: string
  userNickname?: string
  nickname?: string
  time: number
  addr?: string
  score?: number
}

interface PhotoItemProps {
  photo: Photo
  onClick: (photo: Photo) => void
}

// 图片项组件
const PhotoItem = React.memo<PhotoItemProps>(({ photo, onClick }) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true)
  }, [])

  const handleImageError = useCallback(() => {
    setImageError(true)
    setImageLoaded(true)
  }, [])

  return (
    <div className="photo-item" onClick={() => onClick(photo)}>
      <div className="photo-wrapper">
        {!imageLoaded && (
          <div className="photo-loading">
            <Spin size="small" />
          </div>
        )}
        {imageError ? (
          <div className="photo-error">
            <PictureOutlined />
            <span>加载失败</span>
          </div>
        ) : (
          <div className="photo-box">
            <img
              src={photo.smallURL}
              alt={photo.file || '照片'}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ display: imageLoaded ? 'block' : 'none' }}
            />
          </div>
        )}
      </div>
      <div className="photo-info">
        <div className="photo-name" title={photo.saveName}>
          {photo.userNickname || photo.nickname || '未命名'}
        </div>
        <div className="photo-time">
          {dayjs
            .unix(photo.time > 9999999999 ? photo.time / 1000 : photo.time)
            .format(CONFIG.photoTimeThisYearFormat)}
        </div>
        <Tooltip placement="top" title={photo.addr}>
          <div className="photo-time">{photo.addr}</div>
        </Tooltip>
        <div className="photo-time">相似度：{photo.score}</div>
      </div>
    </div>
  )
})

interface PhotoGridProps {
  photos: Photo[]
  loading: boolean
  onLoadMore: () => void
  hasMore: boolean
}

// 简单的图片网格组件
const PhotoGrid = React.memo<PhotoGridProps>(
  ({ photos, loading, onLoadMore, hasMore }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const loadingRef = useRef(false) // 添加加载状态引用
    const lastScrollTop = useRef(0) // 记录上次滚动位置
    const [imagePreviewVisible, setImagePreviewVisible] = useState(false)
    const [previewPhoto, setPreviewPhoto] = useState<Photo | null>(null)

    const handleClick = (photo) => {
      setImagePreviewVisible(true)
      setPreviewPhoto(photo)
    }
    const handleCancel = () => setImagePreviewVisible(false)

    // 处理滚动事件，实现无限加载
    const handleScroll = useCallback(() => {
      if (!containerRef.current || loading || !hasMore || loadingRef.current)
        return

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current
      const currentScrollTop = scrollTop

      // 只有向下滚动时才检查加载更多
      if (currentScrollTop <= lastScrollTop.current) {
        lastScrollTop.current = currentScrollTop
        return
      }

      lastScrollTop.current = currentScrollTop

      // 计算距离底部的距离
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)

      // 当距离底部小于5px时触发加载更多（而不是使用百分比）
      if (distanceFromBottom <= 5) {
        loadingRef.current = true
        onLoadMore()

        // 防抖：500ms后重置加载状态
        setTimeout(() => {
          loadingRef.current = false
        }, 500)
      }
    }, [loading, hasMore, onLoadMore])

    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      // 使用节流优化滚动性能
      let ticking = false

      const throttledScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            handleScroll()
            ticking = false
          })
          ticking = true
        }
      }

      container.addEventListener('scroll', throttledScroll, { passive: true })

      return () => {
        container.removeEventListener('scroll', throttledScroll)
      }
    }, [handleScroll])

    // 当loading状态变化时，重置loadingRef
    useEffect(() => {
      if (!loading) {
        loadingRef.current = false
      }
    }, [loading])

    return (
      <>
        <div className="photo-grid" ref={containerRef}>
          {!!photos.length && (
            <div className="photo-grid-content">
              {photos.map((photo, index) => (
                <PhotoItem
                  key={photo.file + index}
                  photo={photo}
                  onClick={(photo) => {
                    // 处理图片点击事件
                    handleClick(photo)
                    console.log('点击图片:', photo)
                  }}
                />
              ))}
            </div>
          )}

          {loading && (
            <div className="loading-more">
              <Spin size="large" />
              <span>加载中...</span>
            </div>
          )}

          {!hasMore && photos.length > 0 && (
            <div className="no-more-data">已加载全部图片</div>
          )}

          {!loading && photos.length === 0 && (
            <div className="no-data">
              <Empty />
            </div>
          )}
        </div>
        <Modal
          open={imagePreviewVisible}
          footer={null}
          onCancel={handleCancel}
          closable
          closeIcon={
            <CloseOutlined style={{ color: 'white', fontSize: '24px' }} />
          }
          centered
          styles={{
            body: {
              padding: 0,
              textAlign: 'center',
              background: 'black',
              fontSize: '0'
            }
          }}
          width="100%"
        >
          <img
            onClick={handleCancel}
            src={previewPhoto?.largeURL}
            style={{
              maxWidth: '100%',
              height: '100vh',
              objectFit: 'contain'
            }}
          />
        </Modal>
      </>
    )
  }
)

export default () => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)

  const [loading, setLoading] = useState(false)
  const [photos, setPhotos] = useState<Photo[]>([])
  const [lastPageCond, setLastPageCond] = useState('')
  const [hasMore, setHasMore] = useState(true)
  const [filterValue, setFilterValue] = useState({
    inputValue: '',
    filter: ''
  })

  useEffect(() => {
    getAllPhotos('new')
  }, [])

  const getAllPhotos = async (type: 'new' | 'append') => {
    try {
      setLoading(true)
      const res = await dispatch(
        getWorkgroupPhotos({
          groupID: groupID,
          filter: {
            searchKeywords: [],
            mediaType: null,
            sourceType: null,
            times: [
              { start: '2025-03-01 00:00:00', end: '2025-05-20 23:59:59' }
            ],
            userIDList: [],
            departmentIDList: ['0'],
            watermarks: [],
            locations: [],
            useSegment: true,
            useSynonym: false,
            useSuggestedSearch: true,
            suggestedSearchWords: [],
            locationRecommend: {},
            excludeKeywords: []
          },
          isPreciseSearch: false,
          lastPageCond: lastPageCond
        })
      )
      setLastPageCond(res.lastPageCond)
      if (type === 'new' && res.photos) {
        setPhotos(res.photos)
      } else if (res.photos) {
        setPhotos([...photos, ...res.photos])
      }
      setLoading(false)
    } catch (error) {
      console.log('error', error)
    }
  }

  const getAISearchPhotos = async (type: 'new' | 'append') => {
    try {
      setLoading(true)
      const res = await dispatch(
        getWorkgroupPhotosByAiSearch({
          groupID: groupID,
          filter: {
            searchKeywords: [],
            mediaType: null,
            sourceType: null,
            times: [
              { start: '2025-03-01 00:00:00', end: '2025-05-20 23:59:59' }
            ],
            userIDList: [],
            departmentIDList: ['0'],
            watermarks: [],
            locations: [],
            useSegment: true,
            useSynonym: false,
            useSuggestedSearch: true,
            suggestedSearchWords: [],
            locationRecommend: {},
            excludeKeywords: []
          },
          isPreciseSearch: false,
          lastPageCond: lastPageCond,
          text: filterValue.inputValue,
          sort: +filterValue.filter
        })
      )
      setLastPageCond(res.lastPageCond)
      if (type === 'new' && res.photos) {
        setPhotos(res.photos)
      } else if (res.photos) {
        setPhotos((prevPhotos) => [...prevPhotos, ...res.photos]) // 使用函数式更新
      } else {
        message.info('未检测到相关信息')
      }
      setLoading(false)
    } catch (error) {
      console.log('error', error)
    }
  }

  const handleLoadMore = useCallback(() => {
    console.log('handleLoadMore', !loading, lastPageCond)
    if (!loading) {
      if (lastPageCond) {
        if (filterValue.inputValue) {
          getAISearchPhotos('append') // 明确传递追加类型
        } else {
          getAllPhotos('append') // 明确传递追加类型
        }
      } else {
        setHasMore(false)
      }
    }
  }, [loading, lastPageCond, filterValue.inputValue, photos])

  const handleSubmit = (e) => {
    // 重新搜索时重置分页
    setHasMore(true)
    setPhotos([])
    setLastPageCond('')
    if (filterValue.inputValue) {
      getAISearchPhotos('new')
    } else {
      getAllPhotos('new')
    }
  }

  return (
    <Layout className="ai-search-photo">
      <Content className="ai-search-photo-content">
        <div className="search-container">
          <Form layout="inline">
            <Row>
              <FormItem label="搜索内容：">
                <Input
                  type="text"
                  onChange={(e) => {
                    setFilterValue({
                      ...filterValue,
                      inputValue: e.target.value.trim()
                    })
                  }}
                  value={filterValue.inputValue}
                  style={{ width: '336px', marginRight: '20px' }}
                  placeholder="请输入搜索关键词"
                />
              </FormItem>
              {filterValue.inputValue && (
                <FormItem label="筛选条件：">
                  <Select
                    style={{ width: '150px', marginRight: '20px' }}
                    onChange={(value) => {
                      setFilterValue({ ...filterValue, filter: value })
                    }}
                    value={filterValue.filter}
                  >
                    <Option value="0">按照相似度排序</Option>
                    <Option value="1">按照时间排序</Option>
                  </Select>
                </FormItem>
              )}
              <FormItem>
                <Button type="primary" onClick={handleSubmit}>
                  搜索
                </Button>
              </FormItem>
            </Row>
          </Form>
        </div>
        <PhotoGrid
          photos={photos}
          loading={loading}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
      </Content>
    </Layout>
  )
}
